import styled from "@emotion/styled";
import { Trans, useLingui } from "@lingui/react/macro";
import { Dispatch, useCallback } from "react";

import { usePostQuestionAttachmentsMutation } from "@/queries/attachments/mutations";
import { PublicQuestion, TestType } from "@/queries/types";
import { useRouter } from "next/router";
import { useFlashMessages } from "../flash-messages";
import { UploadButton } from "../upload-button";
import { Action } from "./reducer";

interface QuestionAttachmentUploadProps {
  testType: TestType;
  question?: PublicQuestion;
  dispatch: Dispatch<Action>;
}

export function QuestionAttachmentUpload({
  testType,
  question,
  dispatch,
}: QuestionAttachmentUploadProps) {
  const { t } = useLingui();
  const { query } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const testSlug = query.hashedTestId as string;
  const questionAttachmentsMutation = usePostQuestionAttachmentsMutation();
  const { addMessage } = useFlashMessages();

  const handleFileSelection = useCallback(
    async (event) => {
      const target = event.target as HTMLInputElement;
      const files = target.files!;

      if (files.length && question) {
        questionAttachmentsMutation.mutate(
          {
            job_opening_slug: jobOpeningSlug,
            test_slug: testSlug,
            question_id: question.test_question_id.toString(),
            upload: files[0],
          },
          {
            onSuccess: (attachment) => {
              dispatch({
                type: "upload_answer_attachment",
                payload: {
                  questionId: question.test_question_id.toString(),
                  attachment,
                },
              });
              target.value = "";
            },
            onError: () => {
              addMessage({ type: "upload_error" });
            },
          },
        );
      }
    },
    [
      dispatch,
      question,
      questionAttachmentsMutation,
      jobOpeningSlug,
      testSlug,
      addMessage,
    ],
  );

  if (
    testType !== "homework" ||
    !question ||
    question?.require_attachment === null
  ) {
    return null;
  }

  return (
    <Container>
      <Text>
        <Trans>The file upload is</Trans>{" "}
        {question?.require_attachment ? t`mandatory` : t`optional`}
      </Text>
      <UploadButton
        onChange={handleFileSelection}
        uploading={questionAttachmentsMutation.isPending}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    margin-top: -4px;
  }
`;

const Text = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.purple[60]};
`;
